import React, { useEffect, useState } from 'react';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import MonthPicker from '@/components/MonthPicker';

/**
 * Отчёт «Проведённые дополнительные занятия за месяц» — учительская
 * версия админского `extras-by-teacher-monthly`. Бэк всегда фильтрует
 * по `teacher_id = me`, поэтому фильтра «учитель» здесь нет — выбираем
 * только месяц.
 *
 * Структура данных идентична админскому отчёту: `teachers` всегда
 * содержит ноль или одну запись (самого учителя), внутри — разделы
 * «Индивидуальные» и «Групповые» по предметам.
 */
const URL_SCHEMA = { month: '' };

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '₽';
}
function shortTime(t) { return (t || '').slice(0, 5); }
function currentMonthIso() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

export default function TeacherExtrasMonthlyReport() {
  const { tweaks, updateTweak } = useShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);

  useEffect(() => {
    if (!params.month) setParams({ month: currentMonthIso() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const build = async () => {
    if (!params.month) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({ month: params.month });
      const res = await api.get('/api/teacher/reports/extras-monthly?' + qs.toString());
      setData(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось построить отчёт');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!params.month) return;
    build();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.month]);

  // У учителя в ответе всегда максимум один блок «teachers» — он сам.
  const me = data?.teachers?.[0] || null;

  return (
    <>
      <Topbar
        breadcrumb={[
          { label: 'Разное', to: '/teacher/reports' },
          { label: 'Отчёты', to: '/teacher/reports' },
          'Проведённые доп. занятия',
        ]}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row" style={{ alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>Месяц</label>
            <MonthPicker
              value={params.month || ''}
              onChange={(v) => setParams({ month: v })}
            />
          </div>

          <button className="btn btn--primary btn--sm" onClick={build} disabled={loading}>
            {loading ? 'Считаем…' : 'Сформировать'}
          </button>

          <div className="filterbar__spacer" />
          {data && me && (
            <div className="filterbar__meta">
              Итого к выплате: <b>{fmtMoney(me.totalAmount)}</b>
            </div>
          )}
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {!data && !loading && !error && (
          <div className="ref-empty" style={{ padding: 32 }}>Выберите месяц.</div>
        )}
        {data && !me && !loading && (
          <div className="ref-empty" style={{ padding: 32 }}>
            За выбранный месяц проведённых вами занятий нет.
          </div>
        )}

        {data && me && (
          <div className="reports-content">
            <TeacherBlock teacher={me} daysInMonth={data.daysInMonth} />
          </div>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Блоки рендеринга — структурно идентичны админскому отчёту. Имя     */
/* учителя в шапке не показываем — там и так понятно, что это я.      */
/* ------------------------------------------------------------------ */
function TeacherBlock({ teacher, daysInMonth }) {
  return (
    <div className="report-teacher">
      {teacher.individual.length > 0 && (
        <div className="report-teacher__section">
          <h4 className="report-teacher__section-title">Индивидуальные занятия</h4>
          {teacher.individual.map((sub) => (
            <SubjectIndividual key={sub.subjectId} sub={sub} daysInMonth={daysInMonth} />
          ))}
        </div>
      )}

      {teacher.group.length > 0 && (
        <div className="report-teacher__section">
          <h4 className="report-teacher__section-title">Групповые занятия</h4>
          {teacher.group.map((sub) => (
            <SubjectGroup key={sub.subjectId} sub={sub} daysInMonth={daysInMonth} />
          ))}
        </div>
      )}

      <div className="report-teacher__total">
        <span>Итого к выплате:</span>
        <b className="mono">{fmtMoney(teacher.totalAmount)}</b>
      </div>
    </div>
  );
}

function SubjectIndividual({ sub, daysInMonth }) {
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  return (
    <div className="report-subject">
      <div className="report-subject__head">
        <span><b>Предмет:</b> {sub.subjectName}</span>
        <span><b>Ставка за час:</b> {Number(sub.priceTeacher).toLocaleString('ru-RU')}</span>
      </div>
      <div className="table-wrap">
        <table className="table report-grid">
          <thead>
            <tr>
              <th className="report-grid__subj">Ученик</th>
              {days.map((d) => (
                <th key={d} className="report-grid__day">{String(d).padStart(2, '0')}</th>
              ))}
              <th className="report-grid__visits">Посещений</th>
              <th className="report-grid__sum">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {sub.students.map((s) => (
              <tr key={s.studentId}>
                <td className="report-grid__subj">{s.studentName}</td>
                {days.map((d) => (
                  <td key={d} className="report-grid__day mono">
                    {s.byDay[String(d)] || ''}
                  </td>
                ))}
                <td className="report-grid__visits mono"><b>{s.visits}</b></td>
                <td className="report-grid__sum mono"><b>{fmtMoney(s.amount)}</b></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={daysInMonth + 1} style={{ textAlign: 'right' }}><b>Итого</b></td>
              <td className="mono"><b>{sub.totalVisits}</b></td>
              <td className="mono"><b>{fmtMoney(sub.totalAmount)}</b></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function SubjectGroup({ sub, daysInMonth }) {
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  return (
    <div className="report-subject">
      <div className="report-subject__head">
        <span><b>Предмет:</b> {sub.subjectName}</span>
        <span><b>Ставка за ученик/час:</b> {Number(sub.priceTeacher).toLocaleString('ru-RU')}</span>
      </div>
      <div className="table-wrap">
        <table className="table report-grid">
          <thead>
            <tr>
              <th className="report-grid__subj">Время</th>
              {days.map((d) => (
                <th key={d} className="report-grid__day">{String(d).padStart(2, '0')}</th>
              ))}
              <th className="report-grid__visits">Занятий</th>
              <th className="report-grid__sum">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {sub.slots.map((slot) => (
              <tr key={slot.timeId}>
                <td className="report-grid__subj mono">
                  {shortTime(slot.timeStart)}–{shortTime(slot.timeEnd)}
                </td>
                {days.map((d) => {
                  const cell = slot.byDay[String(d)];
                  return (
                    <td key={d} className="report-grid__day mono">
                      {cell ? `${cell.lessons}(${cell.students})` : ''}
                    </td>
                  );
                })}
                <td className="report-grid__visits mono">
                  <b>{slot.totalLessons}</b>{' '}
                  <span className="muted">({slot.totalStudents})</span>
                </td>
                <td className="report-grid__sum mono"><b>{fmtMoney(slot.amount)}</b></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={daysInMonth + 1} style={{ textAlign: 'right' }}><b>Итого</b></td>
              <td className="mono">
                <b>{sub.totalLessons}</b> <span className="muted">({sub.totalStudents})</span>
              </td>
              <td className="mono"><b>{fmtMoney(sub.totalAmount)}</b></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
